import { useMemo } from 'react';

import { RyeconColorSnowflake } from '@dbt-labs/sourdough';

import {
  EnvironmentDeploymentType,
  SearchFieldType,
  SearchPlatform,
} from '@/__generated__/graphql';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useWarehouse } from '@/context/WarehouseProvider';
import { useAccountSearchResultsWrapper } from '@/hooks/discovery/accountSearchWrapper';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { UrlEnvironmentType } from '@/typings/url';
import { TextIconPair } from '../components/Header';

interface UseWarehouseHeaderIconProps {
  uniqueId: ParsedUniqueId;
  environmentType: UrlEnvironmentType;
}

export const useWarehouseHeaderIcon = ({
  uniqueId,
  environmentType,
}: UseWarehouseHeaderIconProps): TextIconPair | null => {
  const { accountId, hasAccountFeature } = useDbtCloudAccount();
  const { environmentId } = useDbtCloudProject();
  const { warehouseType: warehouseTypeFromProvider, name: assetNameFromProvider } =
    useWarehouse();

  const searchQuery = useAccountSearchResultsWrapper({
    accountId,
    filter: {
      assetTypes: [SearchPlatform.Dbt],
      fields: [SearchFieldType.Name],
      query: '',
      dbtEnvs: [environmentType as EnvironmentDeploymentType],
      urns: null,
      resourceTypes: [],
      tags: [],
      materializationTypes: null,
      modelingLayers: null,
      accessLevels: null,
      projectIds: null,
      warehouseEntityTypes: null,
      environmentIds: [environmentId],
      uniqueIds: [uniqueId?.raw],
    },
    pageSize: 1,
    pause:
      !hasAccountFeature('catalogIngestion') ||
      !uniqueId ||
      Boolean(warehouseTypeFromProvider || assetNameFromProvider),
  });

  return useMemo(() => {
    if (warehouseTypeFromProvider && assetNameFromProvider) {
      return {
        ryecon: RyeconColorSnowflake,
        text: assetNameFromProvider,
      };
    }
    if (searchQuery.fetching) return null;
    const warehouseAsset = searchQuery.data?.[0]?.hit.warehouseAsset;
    if (!warehouseAsset?.warehouseType || !warehouseAsset?.name) return null;

    return {
      ryecon: RyeconColorSnowflake,
      text: warehouseAsset.name,
    };
  }, [
    assetNameFromProvider,
    searchQuery.data,
    searchQuery.fetching,
    warehouseTypeFromProvider,
  ]);
};
